// Clients (annuaire, SA-1/SA-2) — porté de la maquette Super Admin v2 : liste (Par client / Tous les magasins /
// Brouillons) + fiche détail à onglets (Magasins · Identité · Licences & matériel · Utilisateurs · Blocages).
// ⚠️ §23.0 : on n'affiche QUE des données réelles (tenant/store/device/license) ; ce qui vit dans l'IdP
// (blocages/kill-switch) est marqué « à venir », jamais simulé. Les comptes CLIENTS sont réels depuis
// 2026-08-10 : identité chez l'IdP, rattachement et périmètre ici (voir ClientAccountsTab).
import { dateLong, dateTimeShort } from '@orgazone/date-format';
import { useCallback, useEffect, useId, useState, type ReactNode } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Badge, confirm, showToast, Icon, useLatest, useOverlayPanel } from '@orgazone/ui-web';
import {
  api, ApiError, type TenantRow, type TenantDetail, type StoreRow, type DraftRow,
  type TenantStore, type TenantDevice, type BlockRow, type ActivityEvent, type SupervisionStore, type LegalSignal, type LegalFlag,
} from '../api';
import { formatSiren, formatSiret } from '@orgazone/sync-contract';
import { DefaillanceModal } from './defaillance-modal';
import { ClientEditModal } from './client-edit-modal';
import { ClientLegalBanner, legalFact, useTenantLegal } from './client-legal-watch';
import { DeviceName, deviceRef } from '../device-name';
import { classifyTenantDelete, tenantDeleteTitle } from './tenant-delete-verdict';
import { DataTable } from '../data-table';
import { useAuth, can } from '../auth';
import { OpenBackofficeModal } from './store-reason-modal';
import { Pill as UiPill, daysLeft, fullEanRadical } from '../ui';
import { blockPill, clientSignals, provisioningPill } from '../client-state';
import { healthTone, storeHealth, timeAgo } from '../sync-health';
import { storeStatus } from '../ui';
import { alert } from '@orgazone/ui-web';
import { ProvisioningWizard, type Preset } from './ProvisioningWizard';
import { StoreDetail } from './StoreDetail';
import { DraftDetail } from './DraftDetail';
import { familyTone } from '../event-families';
import { FamilyBadge } from '../family-badge';
import { ClientAccountsTab } from './ClientAccountsTab';
import { EmptyState } from '@orgazone/ui-web';
import { TableScroll } from '../table-scroll';

const fmtDate = dateLong; // « 4 août 2026 » — rendu partagé, tiret si absent
const dfShortStamp = dateTimeShort; // « 28/07 08:12 » (maquette Reçu le) — rendu partagé

// Pastille sémantique (codes couleur de la maquette ok/wa/da/in/vi/ne) — géométrie « tight » (cf. ../ui).
const Pill = ({ k, children }: { k: string; children: ReactNode }) => <UiPill tone={k} shape="tight">{children}</UiPill>;


export function Clients() {
  const { user } = useAuth();
  const canWrite = can(user, 'console.tenant.write'); // création/suppression d'un client = super-admin
  const [tenants, setTenants] = useState<TenantRow[] | null>(null);
  const [stores, setStores] = useState<StoreRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [view, setView] = useState<'clients' | 'stores' | 'drafts'>('clients');
  // ⚠️ LA SÉLECTION VIENT DE L'URL (`/clients/:tenantId/magasin/:storeId`), pas d'un `useState`. C'est
  // ce qui rend « Clients » dans le menu capable de ramener à la liste depuis n'importe quelle fiche :
  // avec un état interne, l'adresse ne changeait pas et rien ne se re-rendait.
  const { tenantId, storeId } = useParams<{ tenantId?: string; storeId?: string }>();
  // ⚠️ `?nouveau=1` OUVRE le wizard, il ne se contente pas d'amener ici. L'accès rapide du tableau
  // de bord promet « Créer un client ou un magasin » : atterrir sur la liste en laissant chercher le
  // bouton serait un bouton qui fait semblant (§23.0). Le paramètre est CONSOMMÉ — sans quoi le
  // wizard se rouvrirait au retour arrière et à chaque rechargement.
  const [chercheNouveau, setChercheNouveau] = useSearchParams();
  const nav = useNavigate();
  const selectedId = tenantId ?? null;
  const selectedStoreId = storeId ?? null;
  /** Ouvre (ou referme) la fiche d'un magasin DANS le client courant. `null` = revenir à la fiche client. */
  const setSelectedStoreId = (id: string | null) => {
    if (!selectedId) return;
    nav(id ? `/clients/${selectedId}/magasin/${id}` : `/clients/${selectedId}`);
  };
  const setSelectedId = (id: string | null) => nav(id ? `/clients/${id}` : '/clients');
  /**
   * Ouvre la fiche d'un magasin depuis la vue « tous les magasins » — client ET magasin d'un coup.
   *
   * ⚠️ UNE SEULE navigation. Enchaîner `setSelectedId` puis `setSelectedStoreId` ne marche pas : la
   * seconde lit le `selectedId` de la passe de rendu COURANTE, encore vide, et n'ouvrirait donc que la
   * fiche client. Les paramètres d'URL ne sont pas un `useState` que l'on empile.
   */
  const ouvrirMagasin = (tid: string, sid: string) => nav(`/clients/${tid}/magasin/${sid}`);
  const [wizard, setWizard] = useState<{ preset?: Preset } | null>(null);
  // Le client dont l'identité se modifie depuis la LISTE (le crayon de sa ligne).
  const [editingTenant, setEditingTenant] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  // La défaillance à trancher (SA-9.3) — ouverte depuis « À traiter » ou depuis la fiche du client.
  const [defaillance, setDefaillance] = useState<LegalSignal | null>(null);
  // Les clients que la veille a signalés (SA-9.3) — la pastille « BODACC » de la liste. ⚠️ Une veille illisible ne pose
  // AUCUNE pastille plutôt que d'en inventer : l'échec se lit sur la fiche et au tableau de bord.
  const [legalFlags, setLegalFlags] = useState<Map<string, LegalFlag>>(new Map());
  useEffect(() => {
    let alive = true;
    api.legalWatch().then((r) => { if (alive) setLegalFlags(new Map(r.flagged.map((x) => [x.tenantId, x]))); }).catch(() => { if (alive) setLegalFlags(new Map()); });
    return () => { alive = false; };
  }, [refreshKey]);

  // Colonnes RÉELLES devenues possibles (BO en place) : Schéma (migrations) + Parc (flotte) + compteur
  // de brouillons de l'onglet. Chaque source en échec vaut null → « — », jamais un zéro inventé (§23.0).
  const [mig, setMig] = useState<Map<string, { version: number | null; upToDate: boolean }> | null>(null);
  const [parc, setParc] = useState<Map<string, { tills: number; devices: number }> | null>(null);
  const [draftCount, setDraftCount] = useState<number | null>(null);
  // La Supervision des magasins — d'où viennent « hors ligne » et « silencieux » dans l'État. `undefined` = en lecture,
  // une chaîne = illisible (le motif), jamais une liste vide inventée.
  const [health, setHealth] = useState<SupervisionStore[] | string | undefined>(undefined);
  const [storeChip, setStoreChip] = useState<'all' | 'uptodate' | 'late'>('all');

  const loadTenants = async () => { try { const { tenants } = await api.tenants(); setTenants(tenants); } catch (e) { setErr((e as Error).message); } };
  const loadStores = async () => { try { const { stores } = await api.stores(); setStores(stores); } catch (e) { setErr((e as Error).message); } };
  useEffect(() => { void loadTenants(); void loadStores(); }, []);
  useEffect(() => {
    if (chercheNouveau.get('nouveau') !== '1') return;
    setWizard({});
    const reste = new URLSearchParams(chercheNouveau);
    reste.delete('nouveau');
    setChercheNouveau(reste, { replace: true });
  }, [chercheNouveau, setChercheNouveau]);
  // L'onglet que demande l'adresse : c'est là que mène un élément « À traiter » (un brouillon à relire ouvre les
  // Brouillons). ⚠️ CONSOMMÉ, comme `?nouveau=1` : resté dans l'adresse, il ramènerait sur cet onglet à chaque
  // changement de paramètre, contre le choix qu'on vient de faire.
  useEffect(() => {
    const vue = chercheNouveau.get('vue');
    if (vue === null) return;
    if (vue === 'brouillons') setView('drafts');
    else if (vue === 'magasins') setView('stores');
    const reste = new URLSearchParams(chercheNouveau);
    reste.delete('vue');
    setChercheNouveau(reste, { replace: true });
  }, [chercheNouveau, setChercheNouveau]);
  // `?defaillance=<id>` (une ligne de « À traiter ») : la FICHE du client s'ouvre avec la décision par-dessus. ⚠️ CONSOMMÉ
  // comme les autres paramètres ; un signalement déjà tranché ou disparu se DIT au lieu d'ouvrir une décision vide.
  // ⚠️ LE PARAMÈTRE VOYAGE JUSQU'À LA FICHE et n'est consommé QUE là : la décision s'ouvre sur la fiche du bon client,
  //    même si la page est remontée en changeant d'adresse.
  useEffect(() => {
    const id = chercheNouveau.get('defaillance');
    if (id === null) return;
    let vivant = true;
    const consommer = () => {
      const reste = new URLSearchParams(chercheNouveau);
      reste.delete('defaillance');
      setChercheNouveau(reste, { replace: true });
    };
    api.legalWatch().then((r) => {
      if (!vivant) return;
      const ev = r.open.find((e) => e.id === id);
      if (!ev) { consommer(); void alert({ title: 'Signalement introuvable', message: 'Il a déjà été tranché, ou la veille ne le porte plus.', tone: 'err' }); return; }
      if (selectedId !== ev.tenantId) { nav(`/clients/${ev.tenantId}?defaillance=${encodeURIComponent(id)}`, { replace: true }); return; }
      consommer();
      setDefaillance(ev);
    }).catch((e) => { if (vivant) { consommer(); void alert({ title: 'Veille des défaillances illisible', message: (e as Error).message, tone: 'err' }); } });
    return () => { vivant = false; };
  }, [chercheNouveau, setChercheNouveau, nav, selectedId]);
  useEffect(() => { api.migrationsStatus().then((r) => setMig(new Map(r.stores.map((s) => [s.id, { version: s.version, upToDate: s.upToDate }])))).catch(() => setMig(null)); }, []);
  useEffect(() => {
    api.devices().then((r) => {
      const m = new Map<string, { tills: number; devices: number }>();
      for (const d of r.devices) {
        if (!d.storeId || d.status === 'revoked') continue;
        const e = m.get(d.storeId) ?? { tills: 0, devices: 0 };
        e.devices += 1;
        if (d.registerNumber != null) e.tills += 1;
        m.set(d.storeId, e);
      }
      setParc(m);
    }).catch(() => setParc(null));
  }, []);
  useEffect(() => {
    let alive = true;
    api.supervision().then((r) => { if (alive) setHealth(r.stores); }).catch((e: unknown) => { if (alive) setHealth((e as Error).message); });
    return () => { alive = false; };
  }, [refreshKey]);
  useEffect(() => { api.drafts().then((r) => setDraftCount(r.drafts.filter((d) => d.status !== 'validated').length)).catch(() => setDraftCount(null)); }, []);

  if (err) return <div className="page"><div className="err">Erreur : {err}</div></div>;
  if (!tenants || !stores) return <div className="page"><div className="muted">Chargement de l'annuaire…</div></div>;

  const selected = tenants.find((t) => t.id === selectedId) ?? null;

  // ⚠️ L'URL DÉSIGNE UN CLIENT QUI N'EXISTE PAS (lien partagé après suppression, identifiant tapé à la
  // main). Retomber silencieusement sur la liste laisserait croire que le lien est mauvais ou que la
  // Console a « perdu » la fiche : on DIT ce qui se passe et on offre la sortie (§23.0).
  if (selectedId && !selected) {
    return (
      <div className="page">
        <EmptyState
          icon="users" title="Client introuvable"
          note={`Aucun client ne correspond à cette adresse. Il a pu être supprimé, ou le lien est incomplet.`}
          action={<button className="ghost btn-ghost" style={{ width: 'auto', padding: '5px 12px' }} onClick={() => nav('/clients')}>← Revenir à la liste</button>}
        />
      </div>
    );
  }

  // Fiche détail
  if (selected) {
    return (
      <div className="page">
      {wizard && <ProvisioningWizard preset={wizard.preset} onClose={() => setWizard(null)} onDone={() => { setWizard(null); showToast('Mise en service effectuée.'); setRefreshKey((k) => k + 1); void loadTenants(); }} />}
        <ClientDetail row={selected} refreshKey={refreshKey} onDecide={setDefaillance} onBack={() => nav('/clients')} onAddStore={() => setWizard({ preset: { mode: 'store', clientId: selected.id, clientName: selected.name } })} onChanged={() => void loadTenants()}
          selectedStoreId={selectedStoreId} onSelectStore={setSelectedStoreId} />
        {defaillance && <DefaillanceModal signal={defaillance} onClose={() => setDefaillance(null)} onDone={() => { setDefaillance(null); setRefreshKey((k) => k + 1); }} />}
      </div>
    );
  }

  // Sous-titre DYNAMIQUE par vue (libellés maquette `clientViewNote`).
  const viewNote = view === 'clients'
    ? 'Une enseigne = un client : ses magasins, ses licences par outil, ses utilisateurs. Un client naît avec son premier magasin.'
    : view === 'stores'
      ? 'Tous les points de vente, tous clients confondus. Un magasin = une base isolée, identifiée par son GLN.'
      : 'Dossiers déposés depuis le formulaire externe ou par un commercial — rien n’est provisionné avant votre validation.';

  // Segmented maquette : conteneur GLASS dans la rangée d'en-tête, pilule active ink-200, compteur
  // UNIQUEMENT sur « Brouillons (N) ».
  // ⚠️ Recette `.seg` de la charte et non un segmenté réécrit : celui d'avant n'avait AUCUN survol —
  // trois boutons parfaitement inertes sous le curseur. Seule la hauteur reste locale (28, maquette).
  const TAB = (v: typeof view, label: string) => (
    <button aria-pressed={view === v} onClick={() => setView(v)} className={view === v ? 'seg active' : 'seg'}
      style={{ height: 28, padding: '0 12px', borderRadius: 'var(--r-xs)' }}>
      {label}
    </button>
  );

  // L'ÉTAT de chaque client (`client-state.ts`) : ses blocages, la Supervision de ses magasins, leur mise en service.
  const healthRows = Array.isArray(health) ? health : null;
  const clientRows = tenants.map((t) => {
    const signals = clientSignals({
      blocks: t.blocks,
      stores: stores.filter((x) => x.tenantId === t.id),
      health: healthRows ? healthRows.filter((h) => h.tenantId === t.id) : null,
    });
    const top = signals[0] ?? null;
    return {
      id: t.id,
      onClick: () => setSelectedId(t.id),
      cells: {
        name: {
          // ⚠️ Plus de tuile d'initiales (demande utilisateur) : le nom et le SIREN identifient déjà
          // la ligne, et la vignette prenait 40 px de large pour ne rien ajouter à la lecture.
          v: (
            <div>
              <div style={{ fontWeight: 600 }}>{t.name}</div>
              <div className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)' }}>{t.siren ? `SIREN ${t.siren}` : '—'}</div>
              {/* Signalé par la veille : rouge tant qu'une annonce attend sa décision, neutre une fois tranché. */}
              {legalFlags.has(t.id) && (
                <div style={{ marginTop: 6, lineHeight: 1 }}>
                  <Badge size="xs" tone={legalFlags.get(t.id)!.open > 0 ? 'danger' : 'neutral'}
                    title={legalFlags.get(t.id)!.open > 0 ? 'Défaillance signalée — décision à prendre sur la fiche' : 'Défaillance signalée — décision prise'}>
                    {legalFlags.get(t.id)!.bodacc ? 'BODACC' : 'Sirene'}
                  </Badge>
                </div>
              )}
            </div>
          ),
          text: `${t.name} ${t.siren ?? ''} ${t.businessKind ?? ''}${legalFlags.has(t.id) ? (legalFlags.get(t.id)!.bodacc ? ' BODACC' : ' Sirene') : ''}`,
        },
        trade: { v: <span style={{ color: 'var(--ink600)' }}>{t.businessKind ?? '—'}</span>, text: t.businessKind ?? '' },
        topo: { v: <Pill k={t.topology === 'multi' ? 'in' : 'ne'}>{t.topology === 'multi' ? 'multi-magasin' : 'mono-magasin'}</Pill>, text: t.topology, sortValue: t.topology },
        stores: { v: <span className="mono">{t.storeCount}</span>, text: String(t.storeCount), sortValue: t.storeCount },
        // « Origine » répond à trois questions d'un coup (maquette) : par quel CANAL, par QUI, et QUAND.
        // ⚠️ « — » quand le créateur est inconnu (dossiers antérieurs à la colonne `created_by`) : on ne
        // rétro-attribue pas une responsabilité à un compte qui n'a peut-être rien fait (§23.0).
        origin: { v: <Pill k={t.origin === 'web' ? 'vi' : 'ne'}>{t.origin === 'web' ? 'Web' : 'Interne'}</Pill>, sub: `${t.createdBy ?? '—'} · ${fmtDate(t.createdAt)}`, text: `${t.origin} ${t.createdBy ?? ''}`, sortValue: t.createdAt },
        // Le signal le plus grave, « +n » s'il y en a d'autres, et où. ⚠️ « — » et non une case vide quand il n'y a rien :
        // le tiret dit qu'on a REGARDÉ.
        state: top
          ? {
            v: <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Pill k={top.tone}>{top.label}</Pill>{signals.length > 1 && <span className="mono" style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)' }}>+{signals.length - 1}</span>}</span>,
            sub: top.where ?? undefined,
            text: signals.map((x) => `${x.label} ${x.where ?? ''}`).join(' '),
            sortValue: top.sev,
          }
          : { v: <span style={{ color: 'var(--ink400)' }}>—</span>, text: '', sortValue: 99 },
      },
      // Le crayon modifie le client sans ouvrir sa fiche ; sans le droit, la ligne garde son chevron.
      ...(canWrite
        ? { actions: [{ label: 'Modifier le client', icon: 'edit' as const, name: `Modifier ${t.name}`, onClick: () => setEditingTenant(t.id) }] }
        : { trailing: <span style={{ color: 'var(--ink400)' }}>›</span> }),
    };
  }).sort((a, b) => (a.cells.state.sortValue as number) - (b.cells.state.sortValue as number));

  const storeRowsAll = stores.map((s) => {
    const st = storeStatus(s.status);
    const m = mig?.get(s.id) ?? null;
    const p = parc?.get(s.id) ?? (parc ? { tills: 0, devices: 0 } : null);
    return {
      up: m?.upToDate ?? null,
      row: {
        id: s.id,
        onClick: () => { const t = tenants.find((x) => x.id === s.tenantId); if (t) ouvrirMagasin(t.id, s.id); },
        cells: {
          store: { v: <span style={{ fontWeight: 600 }}>{s.name}</span>, sub: s.gln, text: `${s.name} ${s.gln} ${s.sCode ?? ''}` },
          client: { v: <span style={{ color: 'var(--ink600)' }}>{s.tenantName ?? '—'}</span>, text: s.tenantName ?? '' },
          num: { v: <span className="mono" style={{ fontSize: 'var(--t-dense)' }}>{s.sCode ? `S${s.sCode}` : '—'}</span>, sub: s.radicalEan ? `EAN ${fullEanRadical(s.radicalEan)}` : undefined, text: `${s.sCode ?? ''} ${s.radicalEan ?? ''} 2${s.radicalEan ?? ''}` },
          parc: p === null
            ? { v: <span style={{ color: 'var(--ink400)' }}>—</span>, text: '' }
            : { v: <span className="mono" style={{ fontSize: 'var(--t-dense)' }}>{p.tills ? `${p.tills} caisse${p.tills > 1 ? 's' : ''}` : s.kind === 'depot' ? 'aucune caisse' : '0 caisse'}</span>, sub: `${p.devices} appareil${p.devices > 1 ? 's' : ''}`, text: String(p.tills), sortValue: p.tills },
          type: { v: s.kind === 'depot' ? 'Dépôt' : 'Point de vente', text: s.kind },
          schema: m === null
            ? { v: <span style={{ color: 'var(--ink400)' }}>—</span>, text: '' }
            : { v: <span className="mono" style={{ fontSize: 'var(--t-dense)', color: m.upToDate ? 'var(--ink700)' : 'var(--wa-fg)' }}>{m.version != null ? `v${m.version}` : 'injoignable'}</span>, text: String(m.version ?? ''), sortValue: m.version ?? -1 },
          status: { v: <Pill k={st.k}>{st.label}</Pill>, text: st.label, sortValue: st.label },
        },
        trailing: <span style={{ color: 'var(--ink400)' }}>›</span>,
      },
    };
  });
  const storeRows = storeRowsAll.filter((r) => storeChip === 'all' || (storeChip === 'uptodate' ? r.up === true : r.up === false)).map((r) => r.row);

  const storeChips: { k: typeof storeChip; label: string }[] = [
    // ⚠️ « SCHÉMA » DANS LE LIBELLÉ : la colonne Synchro de la même liste dit aussi « à jour » et « en retard », pour
    //    tout autre chose (le dernier contact des caisses). Deux sens sous les mêmes mots se confondaient au filtre.
    { k: 'all', label: 'Tous' }, { k: 'uptodate', label: 'Schéma à jour' }, { k: 'late', label: 'Schéma en retard' },
  ];

  return (
    <div className="page ozin">
      {editingTenant && <ClientEditModal tenantId={editingTenant} onClose={() => setEditingTenant(null)} onSaved={() => void loadTenants()} />}
      {wizard && <ProvisioningWizard preset={wizard.preset} onClose={() => setWizard(null)} onDone={(cid) => { setWizard(null); showToast('Client / magasin créé.'); void loadTenants(); void loadStores(); if (cid) setSelectedId(cid); }} />}

      {/* En-tête maquette : UNE rangée — titre 22/700 + note dynamique · segmented glass · CTA. */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '12px 16px', marginBottom: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 className="page-title">Clients</h1>
          {/* ⚠️ PAS de `maxWidth` ici : la maquette rend ce sous-titre sans contrainte de mesure, et le
              cap à 620 px qu'on avait ajouté le cassait en deux lignes alors que le texte n'en occupe
              que ~780 sur une colonne bien plus large. Une limite de mesure sert à éviter une ligne
              interminable — elle n'a pas à couper un texte qui tient déjà. */}
          <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink500)', marginTop: 3 }}>{viewNote}</div>
        </div>
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', gap: 2, padding: 3, background: 'var(--glass)', backdropFilter: 'var(--glass-blur)', border: '1px solid var(--ink200)', borderRadius: 'var(--r-xs)' }}>
          {TAB('clients', 'Par client')}
          {TAB('stores', 'Tous les magasins')}
          {TAB('drafts', `Brouillons${draftCount != null ? ` (${draftCount})` : ''}`)}
        </div>
        {canWrite && <button className="btn btn-cta sm" style={{ height: 34, padding: '0 15px' }} onClick={() => setWizard({})}>Nouveau client</button>}
      </div>


      {view === 'clients' && typeof health === 'string' && (
        <div className="note" role="status" style={{ marginBottom: 10 }}>
          Supervision illisible ({health}) : l’État ne compte ni les caisses hors ligne ni les silencieuses.
        </div>
      )}
      {view === 'clients' && (
        <DataTable
          unit="client"
          searchPlaceholder="Client, GLN, métier…"
          pageSize={10}
          emptyIcon="users"
          emptyTitle="Aucun client"
          emptyNote="Les entreprises clientes apparaîtront ici — « Nouveau client » lance le wizard (identité Sirene, magasins, mise en service)."
          cols={[
            { key: 'name', label: 'Client' }, { key: 'state', label: 'État', sortable: true }, { key: 'trade', label: 'Métier / licences' },
            { key: 'topo', label: 'Topologie' }, { key: 'stores', label: 'Magasins' }, { key: 'origin', label: 'Origine' },
          ]}
          rows={clientRows}
        />
      )}

      {view === 'stores' && (
        <>
          {/* Chips maquette (Schéma à jour / en retard = état de SCHÉMA, réel via migrations ; « Hors-ligne »
              viendra avec l'état de sync remonté du BO — jamais simulé, §23.0). */}
          <div style={{ display: 'flex', gap: 5, marginBottom: 10, flexWrap: 'wrap' }}>
            {storeChips.map((c) => (
              <button aria-pressed={storeChip === c.k} key={c.k} onClick={() => setStoreChip(c.k)} className={storeChip === c.k ? 'chip active' : 'chip'} style={{ height: 26, padding: '0 11px', borderRadius: 'var(--r-xs)', fontSize: 'var(--t-meta)' }}>
                {c.label}
              </button>
            ))}
          </div>
          <DataTable
            unit="magasin"
            searchPlaceholder="Magasin, GLN, S…, client…"
            pageSize={25}
            emptyIcon="store"
            emptyTitle="Aucun magasin"
            emptyNote="Chaque magasin créé (avec son GLN et sa numérotation) apparaîtra ici, tous clients confondus."
            cols={[
              { key: 'store', label: 'Magasin' }, { key: 'client', label: 'Client' }, { key: 'num', label: 'N° / radical' },
              { key: 'parc', label: 'Parc' }, { key: 'type', label: 'Type' }, { key: 'schema', label: 'Schéma' }, { key: 'status', label: 'Statut' },
            ]}
            rows={storeRows}
          />
        </>
      )}

      {view === 'drafts' && <DraftsView />}
    </div>
  );
}

/**
 * Onglet HISTORIQUE de la fiche client — le journal de la Console, filtré sur ce client et ses magasins.
 *
 * ⚠️ Le filtre est fait CÔTÉ SERVEUR, par identifiants : chercher le nom du client dans le texte de
 * l'évènement serait faux au premier renommage, et attraperait tout client dont le nom en contient
 * un autre.
 * ⚠️ Ne montre que les opérations de la CONSOLE. Les évènements métier du back-office ne passent pas
 * par ce journal — le sous-titre le dit, sinon l'absence d'une vente se lirait comme un trou.
 */
function ClientHistoryTab({ tenantId }: { tenantId: string }) {
  const [rows, setRows] = useState<ActivityEvent[] | null>(null);
  const [since, setSince] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [fam, setFam] = useState('tout');
  const [page, setPage] = useState(0);
  const PAR_PAGE = 8;

  useEffect(() => {
    let vivant = true;
    api.tenantActivity(tenantId)
      .then((r) => { if (vivant) { setRows(r.events); setSince(r.since); setErr(null); } })
      .catch((e) => { if (vivant) { setRows(null); setErr((e as Error).message); } });
    return () => { vivant = false; };
  }, [tenantId]);

  // ⚠️ Seules les familles RÉELLEMENT présentes chez ce client sont proposées : offrir un filtre qui
  // ne peut rien donner fait douter de la donnée plutôt que du filtre.
  const familles = rows ? [...new Set(rows.map((e) => e.family))] : [];
  const filtrees = (rows ?? []).filter((e) => fam === 'tout' || e.family === fam);
  const pages = Math.max(1, Math.ceil(filtrees.length / PAR_PAGE));
  const p = Math.min(page, pages - 1);
  const visibles = filtrees.slice(p * PAR_PAGE, p * PAR_PAGE + PAR_PAGE);

  if (err) return <div className="dcard" style={{ padding: 18 }}><span className="err">Historique indisponible : {err}</span></div>;
  if (!rows) return <div className="dcard" style={{ padding: 18 }}><span className="muted">Lecture de l’historique…</span></div>;

  return (
    <div className="dcard" style={{ overflow: 'hidden' }}>
      <div style={{ padding: '13px 18px', borderBottom: '1px solid var(--ink200)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 'var(--t-title)', fontWeight: 700 }}>Historique du client</div>
          <div className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)', marginTop: 2 }}>opérations de la console uniquement — client et magasins confondus</div>
        </div>
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
          {['tout', ...familles].map((k) => (
            <button key={k} type="button" onClick={() => { setFam(k); setPage(0); }}
              className={fam === k ? 'chip active' : 'chip'} style={{ height: 25, borderRadius: 'var(--r-xs)', padding: '0 10px', fontSize: 'var(--t-meta)' }}>{k}</button>
          ))}
        </div>
      </div>

      {visibles.length === 0 ? (
        <EmptyState icon="history" title="Aucun évènement sur ce filtre"
          note={fam === 'tout'
            ? `Aucune opération de la console sur ce client. L’historique ne remonte pas avant le ${dateLong(since ?? '')} — les évènements antérieurs n’étaient rattachés à personne.`
            : `Aucune opération « ${fam} » sur ce client. Les autres familles restent visibles avec « tout ».`} />
      ) : visibles.map((e, i) => {
        const ton = familyTone(e.family);
        return (
          <div key={`${e.at}-${i}`} style={{ display: 'flex', alignItems: 'flex-start', gap: 11, padding: '11px 18px', borderBottom: '1px solid var(--ink100)' }}>
            <span style={{ width: 6, height: 6, borderRadius: 'var(--r-pill)', background: ton.fg, flexShrink: 0, marginTop: 6 }} />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink-800)' }}>{e.text}</div>
              {e.meta && <div className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)', marginTop: 2 }}>{e.meta}</div>}
            </div>
            <FamilyBadge family={e.family} />
            <span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink400)', flexShrink: 0, whiteSpace: 'nowrap' }}>{dateTimeShort(e.at)}</span>
          </div>
        );
      })}

      <div style={{ padding: '9px 18px', borderTop: '1px solid var(--ink200)', background: 'var(--ink-50)', display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
        <span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)' }}>
          {filtrees.length === 0 ? '0 sur 0' : `${p * PAR_PAGE + 1}–${Math.min(filtrees.length, (p + 1) * PAR_PAGE)} sur ${filtrees.length}`}
        </span>
        <div style={{ flex: 1 }} />
        <button type="button" className="pg-btn" disabled={p === 0} onClick={() => setPage(p - 1)}>‹</button>
        <button type="button" className="pg-btn" disabled={p >= pages - 1} onClick={() => setPage(p + 1)}>›</button>
      </div>
    </div>
  );
}

// ── Onglet Blocages de la fiche client : les blocages ACTIFS du périmètre du client (abonnement, ses
// magasins, ses caisses) — filtrés par le tenantId résolu côté serveur. Pose/levée → écran /blocages. ──
function ClientBlocksTab({ rows, tenantName, tenantId }: { rows: BlockRow[] | null; tenantName: string; tenantId: string }) {
  const { user } = useAuth();
  // Poser un blocage d'ABONNEMENT est délégué à billing_ops (super-admin inclus) — sinon on n'offre rien.
  const canBlockSub = can(user, 'console.killswitch.subscription');
  const nav = useNavigate();
  const SCOPE_LABEL: Record<BlockRow['scope'], string> = { subscription: 'abonnement', store: 'magasin', device: 'caisse' };
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 300px', gap: 14, alignItems: 'start' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {rows === null ? <div className="dcard" style={{ padding: 18 }}><span className="muted">Blocages indisponibles (source en échec) — réessayez ou passez par l'écran Blocages.</span></div>
          : rows.length === 0 ? (
            <div className="dcard"><EmptyState icon="lock" title="Aucun blocage sur ce client" note="Ni avertissement, ni blocage en cours — rien n'empêche l'encaissement." /></div>
          ) : rows.map((b) => (
            // Une CARTE par blocage (maquette) — bordure rose quand l'encaissement est réellement coupé.
            <div key={b.id} className="dcard" style={{ padding: '14px 16px', border: b.level === 'lock' ? '1px solid color-mix(in srgb, var(--da-fg) 40%, transparent)' : undefined }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                <Pill k={b.level === 'lock' ? 'da' : 'wa'}>{b.level === 'lock' ? 'blocage' : 'avertissement'}</Pill>
                <Pill k="ne">{SCOPE_LABEL[b.scope]}</Pill>
                <div style={{ flex: 1 }} />
                <span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)' }}>{dfShortStamp(b.createdAt)}</span>
              </div>
              <div style={{ fontSize: 'var(--t-control)', fontWeight: 700, marginTop: 8 }}>{b.label}</div>
              <div style={{ fontSize: 'var(--t-dense)', color: 'var(--ink600)', marginTop: 3 }}>{b.reason}</div>
            </div>
          ))}
      </div>
      {/* Aside maquette : poser un blocage depuis la fiche, cible pré-nommée (SA-4.5). */}
      <div className="dcard" style={{ padding: '16px 18px' }}>
        <div style={{ fontSize: 'var(--t-lead)', fontWeight: 700, marginBottom: 8 }}>Poser un blocage</div>
        <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)', lineHeight: 1.55, marginBottom: 12 }}>
          {canBlockSub
            ? 'Motif obligatoire, confirmation nominative, conséquence énoncée avant validation.'
            : "Le blocage d'un abonnement est délégué au rôle administratif — votre compte peut le consulter, pas le poser."}
        </div>
        {canBlockSub && (
          <button
            onClick={() => nav('/blocages', { state: { preset: { scope: 'subscription', targetId: tenantId, targetLabel: tenantName } } })}
            className="btn danger soft" style={{ width: '100%', height: 34, borderRadius: 'var(--r-sm)', fontSize: 'var(--t-body)', fontWeight: 700, cursor: 'pointer' }}>
            Nouveau blocage
          </button>
        )}
        <button className="ghost btn-ghost" style={{ width: '100%', marginTop: 8 }} onClick={() => nav('/blocages')}>Historique complet →</button>
      </div>
    </div>
  );
}

// ── Brouillons de mise en service (réel : store_draft) — liste filtrable + FICHE DE RELECTURE (maquette
// showDraftCard, portée dans DraftDetail.tsx). Vide tant que le formulaire externe n'est pas branché. ──
const DRAFT_STATUS_UI: Record<DraftRow['status'], { k: string; label: string }> = {
  deposited: { k: 'wa', label: 'à relire' }, to_correct: { k: 'vi', label: 'complément demandé' }, validated: { k: 'ok', label: 'validé' },
};
function DraftsView() {
  const { user } = useAuth();
  const [drafts, setDrafts] = useState<DraftRow[] | null>(null);
  const [inviting, setInviting] = useState<null | { draftType: 'client' | 'magasin'; tenantId?: string; tenantName?: string }>(null);
  const [err, setErr] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | DraftRow['status']>('all');
  const [openId, setOpenId] = useState<string | null>(null);
  const load = () => api.drafts().then((r) => setDrafts(r.drafts)).catch((e) => setErr((e as Error).message));
  useEffect(() => { void load(); }, []);

  const open = drafts?.find((d) => d.id === openId) ?? null;
  if (open) return <DraftDetail draft={open} onBack={() => setOpenId(null)} onChanged={() => void load()} />;

  const shown = (drafts ?? []).filter((d) => filter === 'all' || d.status === filter);
  // « Prêts » = libellé maquette pour les dossiers validés (notre statut `validated`).
  const FILTERS: { k: typeof filter; label: string }[] = [
    { k: 'all', label: 'Tous' }, { k: 'deposited', label: 'À relire' }, { k: 'to_correct', label: 'Corrections demandées' }, { k: 'validated', label: 'Prêts' },
  ];

  const inviteModal = inviting && (
    <InviteModal target={inviting} onClose={() => setInviting(null)} />
  );

  const rows = shown.map((d) => {
    const su = DRAFT_STATUS_UI[d.status];
    const gapN = d.sireneGap && typeof d.sireneGap === 'object' ? Object.keys(d.sireneGap as object).length : 0;
    const name = (d.identity as { name?: string })?.name ?? d.id;
    // Pastille SIRET maquette : vérifié (présent sans écart) / écart / absent — l'info « Écart Sirene »
    // de l'ancienne colonne vit désormais ICI (rien de perdu).
    const siretPill = !d.siren && !d.siret
      ? { k: 'ne', label: 'absent' }
      : gapN > 0 ? { k: 'wa', label: `écart (${gapN})` } : { k: 'ok', label: 'vérifié' };
    return {
      id: d.id,
      onClick: () => setOpenId(d.id),
      cells: {
        name: { v: <span style={{ fontWeight: 600 }}>{name}</span>, sub: d.draftType === 'client' ? 'nouveau client' : 'magasin', text: `${name} ${d.siren ?? ''} ${d.siret ?? ''}` },
        siret: { v: <Pill k={siretPill.k}>{siretPill.label}</Pill>, sub: d.siren ?? d.siret ?? undefined, text: `${d.siren ?? ''} ${d.siret ?? ''}`, sortValue: siretPill.label },
        at: { v: <span className="mono" style={{ fontSize: 'var(--t-meta)', whiteSpace: 'nowrap' }}>{dfShortStamp(d.submittedAt)}</span>, text: d.submittedAt, sortValue: d.submittedAt },
        status: { v: <Pill k={su.k}>{su.label}</Pill>, text: su.label, sortValue: su.label },
      },
      trailing: <span style={{ color: 'var(--ink400)' }}>›</span>,
    };
  });

  return (
    <>
      {inviteModal}
      {/* Bandeau maquette : boîte pleine (ink-50 + bordure) avec icône info et la phrase clé en GRAS. */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', border: '1px solid var(--ink200)', background: 'var(--ink-50)', borderRadius: 'var(--r-sm)', padding: '12px 15px', marginBottom: 12, fontSize: 'var(--t-dense)', color: 'var(--ink600)', lineHeight: 1.55 }}>
        <span style={{ color: 'var(--in-fg)', display: 'grid', marginTop: 1 }}><Icon name="info" size={15} /></span>
        <span>Dossiers déposés depuis le formulaire externe ou par un commercial. <b>Aucun ne crée de base tout seul</b> : rien n'est provisionné avant votre validation. Le GLN, les compteurs, le radical EAN et les licences ne sont jamais saisis par le client — nous les attribuons à la validation.</span>
      </div>
      <div style={{ display: 'flex', gap: 5, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        {FILTERS.map((f) => (
          <button aria-pressed={filter === f.k} key={f.k} onClick={() => setFilter(f.k)}
            className={filter === f.k ? 'chip active' : 'chip'}>
            {f.label}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        {/* Un PROSPECT (futur client) reçoit son lien de dépôt d'ici — le canal n'est jamais ouvert au
            tout-venant : chaque lien est nominatif, émis un par un (SPEC SA-2.12). */}
        {can(user, 'console.provisioning.write') && (
          <button className="btn btn-cta sm" style={{ width: 'auto' }} onClick={() => setInviting({ draftType: 'client' })}>
            Inviter un prospect à déposer un dossier
          </button>
        )}
      </div>
      {err ? <div className="err">{err}</div>
        : drafts === null ? <div className="muted">Chargement…</div>
        : (
          <DataTable
            unit="dossier"
            searchPlaceholder="Dossier, client, SIRET…"
            pageSize={15}
            emptyIcon="file"
            emptyTitle={drafts.length === 0 ? 'Aucun brouillon déposé' : 'Aucun brouillon dans ce filtre'}
            emptyNote={drafts.length === 0 ? 'Les dossiers déposés par le formulaire externe arriveront ici pour relecture (contrôle Sirene fait au dépôt).' : 'Changez de filtre pour voir les autres dossiers.'}
            cols={[
              { key: 'name', label: 'Dossier' }, { key: 'siret', label: 'SIRET' }, { key: 'at', label: 'Reçu le' }, { key: 'status', label: 'Statut' },
            ]}
            rows={rows}
          />
        )}
    </>
  );
}

// ── Fiche client : header + onglets (Magasins/Identité/Licences & matériel/Utilisateurs réels ; Blocages à venir) ──
function ClientDetail({ row, refreshKey, onDecide, onBack, onAddStore, onChanged, selectedStoreId, onSelectStore }: {
  row: TenantRow; refreshKey: number; onBack: () => void; onAddStore: () => void;
  /** Ouvre la décision d'une défaillance signalée — la modale vit dans la page. */
  onDecide: (s: LegalSignal) => void;
  onChanged: () => void;
  selectedStoreId: string | null; onSelectStore: (id: string | null) => void;
}) {
  const { user } = useAuth();
  const canWrite = can(user, 'console.tenant.write'); // supprimer un client = super-admin exclusivement
  const [invitingStore, setInvitingStore] = useState(false); // invitation de dépôt (dossier magasin)
  const [detail, setDetail] = useState<TenantDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [tab, setTab] = useState<'stores' | 'ident' | 'lic' | 'users' | 'blocks' | 'history'>('stores');
  const [provId, setProvId] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);
  const [editing, setEditing] = useState(false);

  // ⚠️ `useCallback` et non une fonction recréée à chaque rendu : sans elle, la mettre en dépendance de
  // l'effet relancerait la requête à CHAQUE rendu (boucle) ; l'omettre laisse l'effet capturer un `row.id`
  // périmé. Mémoïsée, elle ne change que quand le client change — l'effet est exact ET stable.
  // ⚠️ UNE RÉPONSE DÉPASSÉE N'ÉCRIT PAS : changer de client pendant la lecture afficherait la fiche de l'ANCIEN sous
  //    l'en-tête du nouveau — magasins, licences et bouton « Supprimer » compris.
  const latest = useLatest();
  const load = useCallback(() => {
    const isLatest = latest();
    return api.tenant(row.id)
      .then((d) => { if (isLatest()) setDetail(d); })
      .catch((e) => { if (isLatest()) setErr((e as Error).message); });
  }, [row.id, latest]);
  useEffect(() => { void load(); }, [load, refreshKey]);

  // Blocages ACTIFS du client — relevés ICI (compteur d'onglet maquette + onglet + check de complétude,
  // UNE source). null = indisponible (jamais un zéro inventé, §23.0).
  // ⚠️ UNE RÉPONSE DÉPASSÉE N'ÉCRIT PAS : changer de client pendant la lecture afficherait sous ce client les blocages
  //    de l'ancien, et celle d'avant un rafraîchissement compterait les blocages d'avant le geste.
  const [blocks, setBlocks] = useState<BlockRow[] | null>(null);
  // La veille des défaillances du client, lue UNE fois pour le bandeau et l'onglet Identité (SA-9.3).
  const legal = useTenantLegal(row.id, refreshKey);
  useEffect(() => {
    let alive = true;
    api.blocks()
      .then((r) => { if (alive) setBlocks(r.active.filter((b) => b.tenantId === row.id)); })
      .catch(() => { if (alive) setBlocks(null); });
    return () => { alive = false; };
  }, [row.id, refreshKey]);

  /**
   * SUPPRIMER LE CLIENT — le geste le plus destructif de la Console, et le plus mal gardé jusqu’ici.
   *
   * ⚠️ IL DÉTRUIT PLUS QU’UNE SUPPRESSION DE MAGASIN : toutes les bases de TOUS ses magasins, leurs
   * données fiscales, leurs instantanés, leurs rôles Postgres, leurs clés d’API et leurs sites EDI. Il
   * n’avait pourtant ni saisie du nom, ni proposition de sauvegarde — là où la suppression d’UN magasin a
   * les deux. L’asymétrie était à l’envers : le geste le plus grave était le moins gardé.
   *
   * ⚠️ L’ORDRE DES DEUX BOÎTES EST LE SUJET : on propose les archives, PUIS on exige le nom. Proposer la
   * sauvegarde après offrirait une archive de bases qui n’existent plus, et un dump qui échoue ARRÊTE
   * tout — on ne détruit pas sur une archive absente.
   */
  const removeClient = async () => {
    const stores = detail?.stores ?? [];
    const n = stores.length || row.storeCount;
    // ⚠️ `dbHost` null = aucune base : rien à sauvegarder pour ce magasin, et le dire évite une boîte
    //    qui promettrait une archive vide.
    const withBase = stores.filter((s) => s.dbHost);

    if (withBase.length > 0) {
      const wantsBackups = await confirm({
        title: `Sauvegarder les ${withBase.length} base(s) de ${row.name} avant de les détruire ?`,
        message: `La suppression détruit la base de CHAQUE magasin du client, ses données FISCALES, ses `
          + `instantanés de migration et son rôle Postgres. Après elle, il ne reste RIEN sur les serveurs — `
          + `les sauvegardes que vous téléchargez ici seront les seules copies.\n\n`
          + `${withBase.map((s) => `· ${s.name} (${s.gln})`).join('\n')}\n\nTélécharger maintenant ?`,
        confirmLabel: `Télécharger ${withBase.length} sauvegarde(s)`,
        cancelLabel: 'Continuer sans sauvegarde',
      });
      // ⚠️ UN SEUL DUMP RATÉ ARRÊTE LE GESTE : poursuivre détruirait la base dont on vient de ne pas
      //    réussir la copie, c’est-à-dire exactement le cas où la copie compte.
      if (wantsBackups) {
        for (const s of withBase) {
          try {
            const { blob, filename } = await api.storeDump(s.id);
            const url = URL.createObjectURL(blob);
            const downloadLink = document.createElement('a');
            downloadLink.href = url; downloadLink.download = filename ?? `${s.gln}.dump`; downloadLink.click();
            URL.revokeObjectURL(url);
            showToast(`Sauvegarde de ${s.name} téléchargée (${Math.round(blob.size / 1024 / 1024 * 10) / 10} Mo).`);
          } catch (e) {
            await alert({
              title: `Sauvegarde de ${s.name} impossible — rien n’a été supprimé`,
              message: `${(e as Error).message}\n\nLe client est intact. Réessayez, ou relancez en choisissant « Continuer sans sauvegarde » en connaissance de cause.`,
              tone: 'err',
            });
            return;
          }
        }
      }
    }

    const ok = await confirm({
      title: `Supprimer définitivement le client ${row.name} ?`,
      message: `Partent définitivement, POUR CHACUN de ses ${n} magasin(s) : la base et toutes ses données `
        + `FISCALES · les instantanés de migration · le rôle Postgres · l’inscription au back-office (donc `
        + `la CLÉ D’API des caisses) · le site EDI et ses liens fournisseurs.\n\n`
        + `${stores.length > 0 ? `${stores.map((s) => `· ${s.name} (${s.gln})${s.dbHost ? '' : ' — sans base'}`).join('\n')}\n\n` : ''}`
        + `Les appareils rentrent au parc, ils ne sont pas supprimés.\n\nAction IRRÉVERSIBLE : aucun écran `
        + `de la Console ne peut rendre ces données.`,
      confirmLabel: `Supprimer le client et ses ${n} magasin(s)`,
      tone: 'danger',
      requireText: row.name,
    });
    if (!ok) return;
    setRemoving(true);
    try {
      const r = await api.deleteTenant(row.id);
      onBack(); onChanged();
      const report = `Client « ${r.name} » supprimé — ${r.storesDeleted} magasin(s), ${r.dbsDropped.length} base(s) supprimée(s), ${r.devicesReleased} appareil(s) remis au parc.`;
      // ⚠️ UNE BASE NON SUPPRIMÉE N’EST PAS UN DÉTAIL DE SUCCÈS : elle reste sur le serveur, avec ses
      // données fiscales, sous un nom que plus aucun magasin ne réclame. Ça s’acquitte (alerte), ça ne
      // s’efface pas tout seul (§24.1quater) — le client, lui, est bien supprimé, d’où le bilan complet.
      if (r.dbErrors.length > 0) {
        await alert({
          title: 'Client supprimé, mais des bases subsistent',
          message: `${report}\n\n${r.dbErrors.length} base(s) n’ont PAS pu être supprimées et restent sur le serveur :\n${r.dbErrors.join('\n')}`,
          tone: 'err',
        });
      } else {
        // ⚠️ TOAST ET NON BANDEAU : on vient de quitter la fiche (`onBack`), donc le message doit
        // survivre au changement d’écran. Un bandeau posé sur une page qu’on abandonne n’est jamais lu.
        showToast(report);
      }
    } catch (e) {
      // 🔴 LE VERDICT SE LIT DANS LE CODE DU SERVEUR, il ne se devine pas au fait qu’il y a eu une
      //    exception. Trois des quatre refus de cette route veulent dire « le client est SUPPRIMÉ, voici
      //    ce qui subsiste » — les traiter tous comme « impossible » faisait lire « rien n’a bougé » sur
      //    un client déjà détruit, et laissait la liste afficher une fiche qui n’existe plus.
      const verdict = classifyTenantDelete(e instanceof ApiError ? e.key : undefined);
      if (verdict !== 'untouched') { onBack(); onChanged(); }
      await alert({
        title: tenantDeleteTitle(verdict, row.name),
        message: verdict === 'unknown'
          ? `${(e as Error).message}\n\nLa Console ne sait pas si la suppression a abouti : vérifiez la liste des clients et le relevé d’orphelins avant de réessayer.`
          : (e as Error).message,
        tone: 'err',
      });
    }
    finally { setRemoving(false); }
  };
  const provision = async (s: TenantStore) => {
    setProvId(s.id);
    try {
      const r = await api.provisionStore(s.id);
      const refs = r.refsNote ?? (r.seededRefs !== undefined ? `${r.seededRefs} lignes de référentiels injectées` : null);
      showToast([`Base « ${r.dbName} » créée — ${s.name} en service.`, refs].filter(Boolean).join(' '));
      await load(); onChanged();
    }
    // ⚠️ MODALE, pas un bandeau : une mise en service qui échoue laisse le magasin INUTILISABLE, et le
    // motif est technique (« Application du schéma : … »). Un bandeau se rate et disparaît à la
    // navigation ; l'utilisateur doit acquitter, et pouvoir recopier le texte pour le transmettre.
    catch (e) {
      void alert({ title: `Mise en service impossible — ${s.name}`, message: (e as Error).message });
    }
    finally { setProvId(null); }
  };

  // Fiche MAGASIN ouverte (maquette : Clients › client › magasin) — remplace la fiche client.
  const storeSel = selectedStoreId && detail ? detail.stores.find((x) => x.id === selectedStoreId) ?? null : null;
  // Magasin disparu (supprimé pendant la consultation) → retour fiche client. ⚠️ Dans un EFFET, jamais
  // pendant le rendu (setState-in-render = avertissement React + risque de boucle).
  const storeGone = !!(selectedStoreId && detail && !storeSel);
  useEffect(() => { if (storeGone) onSelectStore(null); }, [storeGone, onSelectStore]);
  if (selectedStoreId && !detail && !err) return <div className="muted">Chargement de la fiche…</div>;
  if (storeSel && detail) {
    return (
      <>
        <StoreDetail clientId={row.id} clientName={row.name} tenantTrade={detail.tenant.businessKindDefault} store={storeSel} stores={detail.stores} devices={detail.devices}
          onBackClients={onBack} onBackClient={() => onSelectStore(null)}
          onProvision={() => void provision(storeSel)} provBusy={provId === storeSel.id}
          onChanged={() => void load()} />
      </>
    );
  }

  const storesN = detail?.stores.length ?? row.storeCount;
  // Même règle que la liste. Tant que le détail n'est pas là, on ne SAIT pas : on ne prétend donc pas
  // que tout va bien — `enAttente` reste à 0 et le badge dira « Actif » seulement une fois compté.
  // Les pastilles de l'en-tête (maquette du 28/10) : seulement ce qui change la façon de traiter le client.
  const blocked = blocks ? blockPill(blocks) : null;
  const waiting = detail ? provisioningPill(detail.stores) : null;
  // ⚠️ Le COMPTEUR n'est jamais mis en gras avec l'onglet actif (maquette) : c'est une precision,
  // pas une partie du libelle — l'emboldir ferait lire « 12 » comme le titre de l'onglet.
  const TAB = (v: typeof tab, label: string, n?: number) => (
    <button aria-pressed={tab === v} onClick={() => setTab(v)} className={tab === v ? 'tab active' : 'tab'}>
      {label}{n ? <span className="mono" style={{ fontSize: 'var(--t-micro)', color: 'var(--ink-500)', marginLeft: 5, fontWeight: 500 }}>{n}</span> : null}
    </button>
  );

  return (
    <div className="ozin">
      {/* Retour NU (maquette) — pas un bouton bordé. */}
      <button onClick={onBack} className="btn-link" style={{ marginBottom: 12, fontSize: 'var(--t-body)' }}>← Tous les clients</button>

      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <h1 className="page-title">{row.name}</h1>
            {canWrite && (
              <button type="button" className="btn-icon" onClick={() => setEditing(true)} title="Modifier le client" aria-label="Modifier le client">
                <Icon name="edit" size={15} />
              </button>
            )}
          </div>
          {/* ⚠️ SIRET DU SIÈGE et non SIREN ni GLN. Un GLN désigne un lieu qui REÇOIT ou qui VEND —
              ce qu'un siège administratif ne fait ni l'un ni l'autre ; l'EDI route d'ailleurs « par
              GLN (par site), JAMAIS par entité centrale » (spec EDI §2). Le SIRET est l'identifiant
              d'ÉTABLISSEMENT, exactement ce qu'est un siège. Le SIREN, lui, désigne l'entreprise et
              se retrouve plus bas avec les autres mentions administratives. */}
          <div className="mono" style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)', marginTop: 2 }}>{detail?.tenant.siretHq ? `SIRET siège ${detail.tenant.siretHq}` : '— SIRET du siège non renseigné'} · {row.businessKind ?? 'métier ?'} · {storesN} magasin{storesN > 1 ? 's' : ''}</div>
        </div>
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', gap: 7, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* « créé en interne » est le cas par défaut : il ne se dit pas ici (l'historique et la colonne Origine le disent). */}
          {row.origin === 'web' && <Pill k="vi">inscrit en ligne</Pill>}
          {/* ⚠️ « agrégat créé » n'apparaît que si l'annuaire porte VRAIMENT un DSN d'agrégat. Il n'y en a
              aucun aujourd'hui (SA-2.9 : la base de consolidation attend le feed change-log du BO) — la
              mention est donc structurellement absente, et c'est la vérité, pas un oubli d'affichage. */}
          <Pill k={row.topology === 'multi' ? 'in' : 'ne'}>{row.topology === 'multi' ? `multi-magasin${detail?.tenant.hasAggregate ? ' · agrégat créé' : ''}` : 'mono-magasin'}</Pill>
          {waiting && <Pill k={waiting.tone}>{waiting.label}</Pill>}
          {blocked && (
            <button type="button" className="pill-action" onClick={() => setTab('blocks')} title="Voir les blocages">
              <Pill k={blocked.tone}>{blocked.label}</Pill>
            </button>
          )}
          <span style={{ width: 1, height: 20, background: 'var(--ink200)', margin: '0 3px' }} />
          <button className="btn btn-cta sm" style={{ height: 36 }} onClick={onAddStore}>Ajouter un magasin</button>
          {/* L'invitation à DÉPOSER (formulaire externe) : le client remplit lui-même le dossier d'un
              nouveau magasin — rattaché à LUI par le lien, il ne choisit pas son client. */}
          {can(user, 'console.provisioning.write') && (
            <button className="ghost btn-ghost" style={{ width: 'auto', padding: '0 14px', height: 36, fontWeight: 600 }} onClick={() => setInvitingStore(true)}>
              Inviter à déposer un dossier magasin
            </button>
          )}
          {canWrite && <button className="btn danger soft sm" style={{ height: 36 }} onClick={() => void removeClient()} disabled={removing}>{removing ? 'Suppression…' : 'Supprimer'}</button>}
        </div>
        {editing && <ClientEditModal tenantId={row.id} onClose={() => setEditing(false)} onSaved={() => { void load(); onChanged(); }} />}
        {invitingStore && (
          <InviteModal target={{ draftType: 'magasin', tenantId: row.id, tenantName: row.name }} onClose={() => setInvitingStore(false)} />
        )}
      </div>


      {/* La veille des défaillances, en tête de fiche : au-dessus des onglets, pour se lire quel que soit l'onglet ouvert. */}
      <ClientLegalBanner view={legal.view} error={legal.error} tenantId={row.id} tenantName={row.name} onDecide={onDecide} />

      <div style={{ borderBottom: '1px solid var(--ink200)', marginBottom: 16, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {TAB('stores', 'Magasins', storesN)}
        {TAB('ident', 'Identité')}
        {TAB('lic', 'Licences & matériel')}
        {TAB('users', 'Utilisateurs')}
        {TAB('blocks', 'Blocages', blocks?.length || undefined)}
        {/* Pas de compteur : un historique n'a pas de "reste a traiter" — un nombre y serait du bruit. */}
        {TAB('history', 'Historique')}
      </div>

      {err && <div className="err">{err}</div>}
      {!detail && !err ? <div className="muted">Chargement de la fiche…</div> : detail && (
        <>
          {tab === 'stores' && <StoresTab stores={detail.stores} devices={detail.devices} provId={provId} onProvision={provision} onAddStore={onAddStore} onOpen={(id) => onSelectStore(id)} />}
          {tab === 'ident' && <IdentityTab t={detail.tenant} storesN={storesN} blocksN={blocks === null ? null : blocks.length} legal={legalFact(legal.view, legal.error)} onComplete={canWrite ? () => setEditing(true) : null} />}
          {tab === 'lic' && <LicensesTab tenantId={row.id} licenses={detail.licenses} devices={detail.devices} stores={detail.stores} onChanged={() => void load()} />}
          {tab === 'users' && <ClientAccountsTab tenantId={row.id} tenantName={row.name} contactEmail={detail.tenant.contact?.email ?? null} />}
          {tab === 'blocks' && <ClientBlocksTab rows={blocks} tenantName={row.name} tenantId={row.id} />}
          {tab === 'history' && <ClientHistoryTab tenantId={row.id} />}
        </>
      )}
    </div>
  );
}

function StoresTab({ stores, devices, provId, onProvision, onAddStore, onOpen }: { stores: TenantStore[]; devices: TenantDevice[]; provId: string | null; onProvision: (s: TenantStore) => void; onAddStore: () => void; onOpen: (id: string) => void }) {
  // ACCÈS CROSS-TENANT (doctrine d'identité §4ter) : « un bouton par magasin dans la fiche tenant, à
  // côté du GLN ». Il vit dans l'action de LIGNE, à hauteur du GLN qu'elle affiche — un clic depuis la
  // liste, sans passer par la fiche du magasin (qui porte le même geste, en grand).
  // ⚠️ Verbe réservé au super-admin : on n'offre pas un bouton que le serveur refusera (§23.0).
  const { user } = useAuth();
  const canCrossTenant = can(user, 'console.explorer.write');
  // Le magasin dont on demande l'ouverture du back-office : le motif se saisit avant (modale partagée avec la fiche).
  const [boStore, setBoStore] = useState<{ id: string; name: string } | null>(null);
  // Schéma par base (colonne maquette, RÉELLE via l'orchestrateur de migrations) — null = indisponible.
  const [mig, setMig] = useState<Map<string, { version: number | null; upToDate: boolean }> | null>(null);
  useEffect(() => { api.migrationsStatus().then((r) => setMig(new Map(r.stores.map((s) => [s.id, { version: s.version, upToDate: s.upToDate }])))).catch(() => setMig(null)); }, []);
  // ⚠️ La SYNCHRO vient de la supervision (donc du BO), jamais de l'annuaire : la Console ne voit pas
  // les caisses parler. `null` = tournée indisponible → la colonne le DIT au lieu d'afficher « à jour ».
  const [parc, setParc] = useState<SupervisionStore[] | null>(null);
  useEffect(() => { api.supervision().then((r) => setParc(r.stores)).catch(() => setParc(null)); }, []);
  if (stores.length === 0) return <div className="dcard"><EmptyState icon="store" title="Aucun magasin" note="Chaque magasin reçoit son GLN, sa numérotation et sa base à la mise en service." action={<button className="ghost btn-ghost" style={{ width: 'auto', padding: '5px 12px' }} onClick={onAddStore}>+ Ajouter un magasin</button>} /></div>;
  // Parc maquette : CAISSES (n° attribué) en valeur + total d'appareils en sous-ligne — comptes réels.
  const parcOf = (id: string) => {
    const rows = devices.filter((d) => d.storeId === id && d.status !== 'revoked');
    return { tills: rows.filter((d) => d.registerNumber != null).length, devices: rows.length };
  };
  const addrOf = (s: TenantStore) => s.address ? [s.address.voie, [s.address.cp, s.address.ville].filter(Boolean).join(' ')].filter(Boolean).join(', ') : null;
  // ⚠️ STATUT et SYNCHRO répondent à deux questions DIFFÉRENTES, d'où deux colonnes (demande
  // utilisateur) : le statut dit où en est le magasin côté Console (à mettre en service, en service),
  // la synchro dit si ses caisses parlent encore. Un magasin « en service » dont plus aucune caisse
  // ne synchronise est exactement le cas qu'une colonne unique rendrait invisible.
  // ⚠️ Le sous-texte ne sert QUE là où la pastille ne suffit pas — c'est-à-dire quand il apporte un
  // fait que le badge ne porte pas : DEPUIS QUAND (« il y a 3 j ») ou POURQUOI (le message réel de la
  // base muette). Paraphraser le badge (« aucune caisse » → « rien à synchroniser ») ajoutait une
  // ligne à lire pour n'apprendre rien.
  // ⚠️ L'ÉTAT vient de `storeHealth` (le calcul de Supervision : l'appareil le PLUS MUET du magasin) — cette colonne
  // portait sa propre copie des seuils, et le même état s'y lisait « à jour » quand Supervision disait « en ligne ». Ne
  // restent ici que les cas que la tournée ne connaît pas (supervision indisponible, magasin absent de la tournée).
  const synchroOf = (gln: string): { k: string; label: string; sub?: string } => {
    if (parc === null) return { k: 'ne', label: '—', sub: 'supervision indisponible' };
    const m = parc.find((x) => x.gln === gln);
    if (!m) return { k: 'ne', label: 'inconnue du BO' };
    const h = storeHealth(m);
    // Le sous-texte dit DEPUIS QUAND ou POURQUOI : la raison d'un état à problème, sinon l'âge du plus ancien contact.
    return { k: healthTone(h.kind), label: h.label, sub: m.error ? m.error.slice(0, 40) : h.reason ?? (h.worst ? timeAgo(new Date(h.worst.at).toISOString()) : undefined) };
  };

  const rows = stores.map((s) => {
    const st = storeStatus(s.status);
    const addr = addrOf(s);
    const p = parcOf(s.id);
    const m = mig?.get(s.id) ?? null;
    const sy = synchroOf(s.gln);
    return {
      id: s.id,
      onClick: () => onOpen(s.id),
      cells: {
        store: {
          v: <><div style={{ fontWeight: 600 }}>{s.name}</div><div className="mono" style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)' }}>GLN {s.gln}{s.siret ? ` · SIRET ${s.siret}` : ''}</div>{addr && <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)', marginTop: 1 }}>{addr}</div>}</>,
          text: `${s.name} ${s.gln} ${s.siret ?? ''} ${addr ?? ''}`, sortValue: s.name,
        },
        // ⚠️ Numéro et radical sur UNE ligne (« S1 · EAN 200004 ») : ce sont deux identifiants de même
        // rang, pas un titre et sa précision. En sous-ligne, le radical se lisait comme une glose du
        // numéro de magasin alors qu'il préfixe les codes-barres internes — un tout autre usage.
        // ⚠️ Avec son « 2 » de tête : le gencode interne s'écrit 2·RRRRR·NNNNNN·C, et le radical nu
        // (« 00004 ») ne ressemble à rien de ce qu'on lit sur une étiquette.
        num: {
          v: <span className="mono" style={{ fontSize: 'var(--t-dense)' }}>{s.sCode ? `S${s.sCode}` : '—'}{s.radicalEan ? ` · EAN ${fullEanRadical(s.radicalEan)}` : ''}</span>,
          text: `${s.sCode ?? ''} ${s.radicalEan ?? ''} 2${s.radicalEan ?? ''}`, sortValue: s.sCode ?? '',
        },
        parc: { v: <span className="mono" style={{ fontSize: 'var(--t-dense)' }}>{p.tills ? `${p.tills} caisse${p.tills > 1 ? 's' : ''}` : 'aucune caisse'}</span>, sub: `${p.devices} appareil${p.devices > 1 ? 's' : ''}`, sortValue: p.tills },
        kind: { v: s.kind === 'depot' ? 'Dépôt' : 'Point de vente', text: s.kind, sortValue: s.kind },
        schema: { v: m === null ? <span style={{ color: 'var(--ink400)' }}>—</span> : <span className="mono" style={{ fontSize: 'var(--t-dense)', color: m.upToDate ? 'var(--ink700)' : 'var(--wa-fg)' }}>{m.version != null ? `v${m.version}` : 'injoignable'}</span>, sortValue: m?.version ?? -1 },
        status: { v: <Pill k={st.k}>{st.label}</Pill>, text: st.label, sortValue: st.label },
        sync: { v: <Pill k={sy.k}>{sy.label}</Pill>, sub: sy.sub, text: sy.label, sortValue: sy.label },
      },
      // ⚠️ Pas de chevron sur les lignes sans action : la ligne entière est déjà cliquable et le
      // survol le montre. Un chevron y ajoutait une cible qui n'en est pas une.
      trailing: s.status === 'provisioning'
        ? <button className="btn-ghost sm" disabled={provId === s.id} onClick={(e) => { e.stopPropagation(); onProvision(s); }}>{provId === s.id ? 'Création…' : 'Mettre en service'}</button>
        // ⚠️ `boDeclaredAt` COMPTE AUTANT QUE `status` (correctif 2026-08-16, relecture). Un magasin
        // « actif » que le back-office n'a pas encore dans son registre ne peut PAS être ouvert : le
        // ticket serait refusé pour magasin inconnu. Le bouton était rendu actif avec une infobulle qui
        // promettait l'ouverture — « un bouton qui fait semblant » (§23.0). Désactivé, il DIT pourquoi,
        // ce qui indique aussi le geste qui débloque : mettre le magasin en service.
        : canCrossTenant && s.status === 'active'
          // ⚠️ `stopPropagation` : la ligne entière ouvre la fiche du magasin — sans lui, on ouvrirait
          // le back-office ET on changerait d'écran derrière.
          ? (
            <button
              className="btn-ghost sm"
              disabled={!s.boDeclaredAt}
              title={s.boDeclaredAt
                ? "Ouvre le back-office de ce magasin avec votre compte Orgazone, dans un nouvel onglet. Accès borné à ce magasin, journalisé et visible du client."
                : "Ce magasin n'est pas encore déclaré au back-office : il n'y a rien à ouvrir. Déclarez-le depuis sa fiche."}
              onClick={(e) => { e.stopPropagation(); setBoStore({ id: s.id, name: s.name }); }}
            >
              Ouvrir le BO
            </button>
          )
          : undefined,
    };
  });

  return (
    <>
    {boStore && <OpenBackofficeModal store={boStore} onClose={() => setBoStore(null)} />}
    <DataTable
      unit="magasin"
      searchPlaceholder="Magasin, GLN, SIRET, adresse…"
      pageSize={10}
      emptyIcon="store"
      emptyTitle="Aucun magasin"
      emptyNote="Chaque magasin reçoit son GLN, sa numérotation et sa base à la mise en service."
      cols={[
        // ⚠️ La version de SCHÉMA ferme le tableau : c'est une donnée d'exploitation (a-t-on migré
        // cette base ?), pas un attribut du magasin. Placée au milieu, elle coupait la lecture entre
        // ce que le magasin EST et l'état de son service.
        { key: 'store', label: 'Magasin' }, { key: 'num', label: 'N° / radical' }, { key: 'parc', label: 'Parc' },
        { key: 'kind', label: 'Type' },
        { key: 'status', label: 'Statut' }, { key: 'sync', label: 'Synchro' }, { key: 'schema', label: 'Schéma' },
      ]}
      rows={rows}
    />
    </>
  );
}

function IdentityTab({ t, storesN, blocksN, legal, onComplete }: {
  t: TenantDetail['tenant']; storesN: number; blocksN: number | null; legal: ReactNode;
  /** Ouvre la modification du client — `null` sans le droit de le modifier. */
  onComplete: (() => void) | null;
}) {
  const NONE = '— non renseigné';
  const siege = t.address ? [t.address.voie, [t.address.cp, t.address.ville].filter(Boolean).join(' ')].filter(Boolean).join(', ') : '';
  /**
   * L'ORDRE DE LA MAQUETTE (28/10) : l'identité légale d'abord, dans l'ordre où on la vérifie, puis le siège et le contact.
   * ⚠️ PAS DE « GLN SIÈGE » : un GLN désigne un lieu qui reçoit ou qui vend, pas un siège administratif — l'EDI route par
   *    site, jamais par entité centrale. Le SIRET du siège est l'identifiant d'établissement.
   * ⚠️ LE NOM COMMERCIAL RESTE (décision du 28/10) : la maquette l'avait retiré, l'assistant le demande.
   */
  const facts: { k: string; v: ReactNode; mono?: boolean }[] = [
    { k: 'Raison sociale', v: t.name },
    { k: 'SIREN', v: t.siren ? formatSiren(t.siren) : NONE, mono: true },
    { k: 'SIRET du siège', v: t.siretHq ? formatSiret(t.siretHq) : NONE, mono: true },
    { k: 'Régime de TVA', v: t.vatFranchise === true ? 'franchise en base (art. 293 B du CGI)' : t.vatFranchise === false ? 'assujetti' : NONE },
    // En franchise sans numéro attribué, l'absence de numéro est la VÉRITÉ, pas un manque.
    { k: 'TVA intracom.', v: t.vatIntra ?? (t.vatFranchise === true ? 'aucun — franchise en base' : NONE), mono: !!t.vatIntra },
    { k: 'Code NAF', v: t.naf ?? NONE, mono: true },
    { k: 'Siège', v: siege || NONE },
    { k: 'E-mail de contact', v: t.contact?.email ?? NONE, mono: !!t.contact?.email },
    { k: 'Nom commercial', v: t.commercialName ?? NONE },
    { k: 'Métier', v: t.businessKindDefault ?? NONE },
    // La veille des défaillances, sous l'identité qu'elle interroge (SA-9.3).
    { k: 'BODACC', v: legal },
    { k: 'Client depuis', v: fmtDate(t.createdAt) },
  ];
  // Complétude honnête (dérivée des champs réellement présents) : ce qui manque est NOMMÉ.
  const legalMissing = [!t.siren && 'SIREN', !t.siretHq && 'SIRET du siège', !t.naf && 'NAF', !t.vatIntra && t.vatFranchise !== true && 'TVA'].filter((x): x is string => !!x);
  const legalOk = legalMissing.length === 0;
  const listed = legalMissing.length > 1 ? `${legalMissing.slice(0, -1).join(', ')} et ${legalMissing.at(-1)}` : legalMissing[0];
  const checks = [
    {
      ok: legalOk as boolean | null, label: 'Identité légale', fix: !legalOk,
      note: legalOk ? (t.vatFranchise === true && !t.vatIntra ? 'SIREN, SIRET et NAF renseignés — franchise en base, sans numéro de TVA' : 'SIREN, SIRET, NAF et TVA renseignés') : `${listed} ${legalMissing.length > 1 ? 'manquants' : 'manquant'} — requis avant la première vente`,
    },
    { ok: (storesN > 0) as boolean | null, label: 'Premier magasin en service', note: storesN > 0 ? `${storesN} magasin(s) rattaché(s)` : 'aucun magasin créé' },
    { ok: (!!t.contact?.email) as boolean | null, label: 'Contact client', note: t.contact?.email ? 'contact renseigné' : 'aucun contact — invitation à envoyer' },
    // 4ᵉ check maquette — null = source blocages indisponible (dit tel quel, jamais un vert inventé §23.0).
    { ok: blocksN === null ? null : blocksN === 0, label: 'Aucun blocage', note: blocksN === null ? 'source blocages indisponible' : blocksN === 0 ? "rien n'empêche l'encaissement" : `${blocksN} blocage(s)/avertissement(s) actif(s)` },
  ];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.2fr) minmax(0,1fr)', gap: 14, alignItems: 'start' }}>
      <div className="dcard" style={{ padding: 18 }}>
        <div style={{ fontSize: 'var(--t-title)', fontWeight: 700, marginBottom: 14 }}>Identité</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: '14px 20px' }}>
          {facts.map((f) => (
            <div key={f.k}>
              <div className="mono" style={{ fontSize: 'var(--t-eyebrow)', letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--ink500)' }}>{f.k}</div>
              {/* Valeur 13/600 encre forte (maquette) — grisée quand « — non renseigné ». */}
              <div className={f.mono ? 'mono' : undefined} style={{ fontSize: f.mono ? 12.5 : 13, fontWeight: 600, color: typeof f.v === 'string' && f.v.startsWith('—') ? 'var(--ink400)' : 'var(--ink900)', marginTop: 3 }}>{f.v}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="dcard" style={{ padding: 18 }}>
        <div style={{ fontSize: 'var(--t-title)', fontWeight: 700, marginBottom: 12 }}>Complétude du dossier</div>
        {checks.map((c) => (
          <div key={c.label} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '8px 0', borderBottom: '1px solid var(--ink100)' }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: c.ok === null ? 'var(--ink300)' : c.ok ? 'var(--ok-fg)' : 'var(--wa-fg)', marginTop: 6, flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontSize: 'var(--t-body)', fontWeight: 600 }}>{c.label}</div><div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)', marginTop: 2 }}>{c.note}</div></div>
            {'fix' in c && c.fix && onComplete && (
              <button type="button" className="btn-link marque" onClick={onComplete} style={{ fontSize: 'var(--t-note)', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}>Compléter →</button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// Libellés HUMAINS des produits (la BD stocke le code) — un code inconnu s'affiche tel quel, jamais caché.
// Outils licenciables (SA-1.6) — même liste que la garde serveur : ouvrir un outil absent d'ici serait refusé.
const LICENSE_PRODUCTS = ['retail', 'chr', 'backoffice', 'orgabusiness', 'mobile-partner', 'edi'];
const PRODUCT_LABEL: Record<string, string> = { retail: 'Sliderz Retail', chr: 'Sliderz CHR', backoffice: 'Back-office', orgabusiness: 'Sliderz Business', 'mobile-partner': 'Sliderz Mobile Partner', edi: 'EDI' };

function LicensesTab({ tenantId, licenses, devices, stores, onChanged }: { tenantId: string; licenses: TenantDetail['licenses']; devices: TenantDevice[]; stores: TenantStore[]; onChanged: () => void }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const canWrite = can(user, 'console.tenant.write');
  const storeName = (id: string | null) => stores.find((s) => s.id === id)?.name ?? '—';
  const [busy, setBusy] = useState(false);

  // ⚠️ Une licence se FERME, elle ne se supprime pas : savoir qu'un outil a été ouvert puis retiré fait
  // partie de l'histoire du contrat. Ouvrir un outil déjà fermé RÉACTIVE sa licence (pas de doublon).
  const openTool = async (product: string) => {
    setBusy(true);
    try { await api.openLicense(tenantId, product); showToast(`${PRODUCT_LABEL[product] ?? product} ouvert.`); onChanged(); }
    catch (e) { void alert({ title: 'Ouverture impossible', message: (e as Error).message }); }
    finally { setBusy(false); }
  };
  const closeTool = async (l: TenantDetail['licenses'][number]) => {
    const label = PRODUCT_LABEL[l.product] ?? l.product;
    const ok = await confirm({
      title: `Fermer ${label} pour ce client ?`,
      // ⚠️ LA LICENCE EST COMMERCIALE : la fermer ne coupe AUCUN accès. Promettre le contraire ferait croire qu'un
      //    utilisateur est sorti alors qu'il entre encore — pour couper, c'est le blocage (onglet Blocages).
      message: "Ce client cesse d'être facturé pour cet outil. La fermeture ne coupe aucun accès : pour l'empêcher d'utiliser l'outil, posez un blocage. La licence reste dans l'historique du contrat.",
      confirmLabel: 'Fermer',
    });
    if (!ok) return;
    setBusy(true);
    try { await api.closeLicense(tenantId, l.id); showToast(`${label} fermé.`); onChanged(); }
    catch (e) { void alert({ title: 'Fermeture impossible', message: (e as Error).message }); }
    finally { setBusy(false); }
  };
  const openable = LICENSE_PRODUCTS.filter((p) => !licenses.some((l) => l.product === p && l.status === 'active'));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Barre de licences maquette : eyebrow mono + chips bordées + note à DROITE (max 340px). */}
      <div className="dcard" style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span className="mono" style={{ fontSize: 'var(--t-eyebrow)', fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--ink500)' }}>licences</span>
        {licenses.length === 0 ? <span style={{ fontSize: 'var(--t-dense)', color: 'var(--ink500)' }}>Aucun outil ouvert — utilisez « Ouvrir un outil ».</span>
          : licenses.map((l) => {
              const on = l.status === 'active';
              return (
                <span key={l.id} style={{ height: 28, padding: '0 11px', borderRadius: 'var(--r-xs)', display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 'var(--t-dense)', fontWeight: 600, border: `1px solid ${on ? 'color-mix(in srgb, var(--teal-500) 35%, transparent)' : 'var(--ink200)'}`, color: on ? 'var(--ink900)' : 'var(--ink500)' }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: on ? 'var(--teal-500)' : 'var(--ink400)' }} />
                  {PRODUCT_LABEL[l.product] ?? l.product}
                  <span className="mono" style={{ fontSize: 'var(--t-micro)', opacity: 0.75 }}>{on ? 'ouverte' : 'fermée'}</span>
                  {canWrite && on && (
                    <button disabled={busy} onClick={() => void closeTool(l)} title="Fermer cet outil"
                      className="btn-icon" style={{ marginLeft: 2 }}>✕</button>
                  )}
                </span>
              );
            })}
        {canWrite && openable.length > 0 && (
          <select aria-label="Ouvrir un outil" disabled={busy} value="" onChange={(e) => { if (e.target.value) void openTool(e.target.value); }}
            style={{ height: 28, padding: '0 9px', borderRadius: 'var(--r-xs)', border: '1px dashed var(--ink300)', background: 'transparent', color: 'var(--ink600)', fontSize: 'var(--t-dense)', fontWeight: 600, cursor: 'pointer' }}>
            <option value="">+ Ouvrir un outil</option>
            {openable.map((p) => <option key={p} value={p}>{PRODUCT_LABEL[p] ?? p}</option>)}
          </select>
        )}
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)', maxWidth: 340, lineHeight: 1.5 }}>Une licence est commerciale : elle sert à la facturation, elle n'ouvre ni ne ferme aucun accès.</span>
      </div>
      <div className="dcard" style={{ overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '13px 16px', borderBottom: '1px solid var(--ink200)', background: 'var(--head)' }}>
          <div>
            <div style={{ fontSize: 'var(--t-title)', fontWeight: 700 }}>Matériel en service</div>
            <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)', marginTop: 2 }}>Caisses, Mobile Partner et postes affectés aux magasins de ce client.</div>
          </div>
          <div style={{ flex: 1 }} />
          <button className="ghost btn-ghost" style={{ width: 'auto', padding: '5px 11px', fontSize: 'var(--t-dense)', color: 'var(--teal)' }} onClick={() => nav('/appareils')}>Registre de la flotte →</button>
        </div>
        {devices.length === 0 ? <EmptyState compact icon="tablet" title="Aucun appareil affecté" note="La flotte (caisses, Mobile Partner, postes) apparaîtra ici dès l’affectation depuis le registre." />
          : <TableScroll minWidth={700}>
              <thead><tr><th>Appareil</th><th>Modèle</th><th>Magasin</th><th>Régime</th><th>Échéance</th><th>Statut</th><th></th></tr></thead>
              <tbody>{devices.map((d) => {
                const dl = d.rentalType && d.rentalType !== 'achat' ? daysLeft(d.rentalEnd) : null;
                const isTill = d.registerNumber != null;
                return (
                  <tr key={d.id}>
                    <td style={{ fontSize: 'var(--t-dense)' }}><b><DeviceName d={d} /></b><div className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)' }}>{d.serial ?? d.id.slice(0, 8)}</div></td>
                    <td>{d.family}{d.model ? ` · ${d.model}` : ''}</td>
                    <td>{storeName(d.storeId)}</td>
                    <td>{d.rentalType ? (d.rentalType === 'achat' ? 'acheté' : `location ${d.rentalType}`) : '—'}</td>
                    <td>{dl === null ? <span style={{ color: 'var(--ink400)' }}>—</span>
                      : <span style={{ color: dl < 0 ? 'var(--da-fg)' : dl < 30 ? 'var(--wa-fg)' : 'var(--ink700)' }}>{dl < 0 ? `${Math.abs(dl)} j dépassés` : `${dl} j`} <span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink400)' }}>({fmtDate(d.rentalEnd)})</span></span>}</td>
                    <td><Pill k={d.status === 'assigned' ? 'ok' : 'ne'}>{d.status}</Pill></td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {/* Kill-switch depuis la LIGNE matériel (maquette + SA-4.5) : cible pré-nommée. */}
                      <button
                        onClick={() => nav('/blocages', { state: { preset: { scope: 'device', targetId: d.id, targetLabel: `${deviceRef(d)} · ${storeName(d.storeId)}` } } })} className="btn-ghost danger" style={{ height: 26, padding: '0 10px', borderRadius: 'var(--r-xs)', fontSize: 'var(--t-meta)', fontWeight: 600 }}>
                        {isTill ? 'Bloquer cette caisse' : 'Bloquer cet appareil'}
                      </button>
                    </td>
                  </tr>
                );
              })}</tbody>
            </TableScroll>}
      </div>
    </div>
  );
}


// ── INVITATION DE DÉPÔT (SA-2.12) : émettre un lien nominatif du formulaire externe. ──────────────
// Deux usages, un composant : PROSPECT (dossier client, depuis les Brouillons) et DOSSIER MAGASIN
// (depuis la fiche client — le lien porte le rattachement, le déposant ne choisit pas son client).
// ⚠️ L'issue de l'e-mail est RESTITUÉE : parti, ou lien à transmettre soi-même — jamais présumée.
function InviteModal({ target, onClose }: { target: { draftType: 'client' | 'magasin'; tenantId?: string; tenantName?: string }; onClose: () => void }) {
  /** Le préfixe des identifiants qui relient chaque champ au texte qui le présente. */
  const fieldId = useId();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [sent, setSent] = useState<null | { url: string; expiresAt: string; mail: { ok: boolean; error?: string } }>(null);
  // ⚠️ ÉCHAP FERME, ET LE FOCUS ENTRE PUIS REVIENT AU DÉCLENCHEUR : au seul pointeur, la modale ne se quittait pas au clavier.
  // Refusé pendant l'émission, comme le clic sur le fond. Le focus va à l'adresse par ce socle, jamais par
  // `autoFocus` : posé avant lui, il serait retenu comme déclencheur et le focus ne reviendrait nulle part.
  const { panelRef } = useOverlayPanel({ open: true, onClose, blockClose: () => busy });

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      setSent(await api.createDraftInvite({ draftType: target.draftType, email: email.trim(), tenantId: target.tenantId }));
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <div className="modalwrap" onClick={busy ? undefined : onClose}>
      <div className="modal" ref={panelRef} role="dialog" aria-modal="true" aria-labelledby={`${fieldId}-title`}
        style={{ width: 'min(94vw, 480px)' }} onClick={(e) => e.stopPropagation()}>
        <h3 id={`${fieldId}-title`}>{target.draftType === 'client' ? 'Inviter un prospect à déposer un dossier' : `Inviter ${target.tenantName ?? 'le client'} à déposer un dossier magasin`}</h3>
        <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)', marginTop: -4, lineHeight: 1.5 }}>
          Le destinataire reçoit un <b>lien personnel</b> vers le formulaire de dépôt.
          {target.draftType === 'magasin' ? ' Le dossier sera rattaché à ce client — le déposant ne le choisit pas.' : ''} Rien n'est créé avant votre validation du dossier.
        </div>

        {!sent && (
          <>
            <div style={{ marginTop: 14 }}>
              <div id={`${fieldId}-0`} style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)', marginBottom: 5 }}>Adresse e-mail du destinataire</div>
              <input aria-labelledby={`${fieldId}-0`} type="email" className="field-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="gerant@exemple.fr" />
            </div>
            {err && <div className="err" style={{ marginTop: 12 }}>{err}</div>}
            <div className="modalfoot">
              <button className="ghost btn-ghost" onClick={onClose} disabled={busy}>Annuler</button>
              <button className="btn btn-cta sm" style={{ width: 'auto' }} disabled={busy || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim())} onClick={() => void submit()}>
                {busy ? 'Émission…' : "Émettre l'invitation"}
              </button>
            </div>
          </>
        )}

        {sent && (
          <div style={{ marginTop: 14 }}>
            {sent.mail.ok ? (
              <div style={{ padding: '10px 13px', borderRadius: 'var(--r-sm)', background: 'var(--ok-bg)', color: 'var(--ok-fg)', fontSize: 'var(--t-note)', lineHeight: 1.5 }}>
                Invitation envoyée à <b>{email.trim()}</b> — le lien expire le {new Date(sent.expiresAt).toLocaleDateString('fr-FR')}.
              </div>
            ) : (
              <div style={{ padding: '10px 13px', borderRadius: 'var(--r-sm)', background: 'var(--wa-bg)', color: 'var(--wa-fg)', fontSize: 'var(--t-note)', lineHeight: 1.5 }}>
                Lien émis, mais <b>l'e-mail n'est pas parti</b> ({sent.mail.error ?? 'raison inconnue'}). Transmettez-le vous-même :
                <div className="mono" style={{ marginTop: 6, overflowWrap: 'anywhere', color: 'var(--ink700)' }}>{sent.url}</div>
              </div>
            )}
            <div className="modalfoot"><button className="btn btn-cta sm" style={{ width: 'auto' }} onClick={onClose}>Fermer</button></div>
          </div>
        )}
      </div>
    </div>
  );
}
